import React from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import AdjustmentTotalsRow from '../AdjustmentTotalsRow';
import TotalsRow, { TotalsRowDescription, TotalsRowValue } from '../TotalsRow';
import formatCurrency from '@afosto/utils/formatCurrency';
import translations from './translations';
import ServiceCostTotalsRow from '../ServiceCostTotalsRow';

const ProjectionList = ({ projection }) => {
  const intl = useIntl();

  const shouldShowVatBelowTotal =
    (projection?.is_including_vat && !projection?.is_vat_shifted) ||
    (!projection?.is_including_vat && projection?.is_vat_shifted);
  const shouldShowVatAsNegative = projection?.is_vat_shifted && projection?.is_including_vat;

  return (
    <>
      {(projection?.services ?? []).map(serviceCost => (
        <ServiceCostTotalsRow
          key={serviceCost?.description}
          serviceCost={serviceCost}
          currency={projection?.currency ?? 'EUR'}
        />
      ))}

      <TotalsRow>
        <TotalsRowDescription variant="body1">
          {intl.formatMessage(translations.totals.subtotal)} (
          {projection.is_including_vat
            ? intl.formatMessage(translations.totals.incl)
            : intl.formatMessage(translations.totals.excl)}
          )
        </TotalsRowDescription>
        <TotalsRowValue variant="subtitle2">
          {formatCurrency((projection?.subtotal ?? 0) / 100, projection?.currency ?? 'EUR')}
        </TotalsRowValue>
      </TotalsRow>
      {(projection?.adjustments ?? []).map(adjustment => (
        <AdjustmentTotalsRow
          key={adjustment?.reference}
          adjustment={adjustment}
          currency={projection?.currency ?? 'EUR'}
        />
      ))}

      {shouldShowVatBelowTotal && (
        <TotalsRow>
          <TotalsRowDescription variant="body1">
            {intl.formatMessage(translations.totals.total)}
          </TotalsRowDescription>
          <TotalsRowValue variant="subtitle2">
            {formatCurrency((projection?.total ?? 0) / 100, projection?.currency ?? 'EUR')}
          </TotalsRowValue>
        </TotalsRow>
      )}

      {(projection?.vat ?? []).map(vat => (
        <TotalsRow key={vat.rate}>
          <TotalsRowDescription variant="body1">
            {shouldShowVatBelowTotal
              ? `${
                  !projection?.is_vat_shifted
                    ? intl.formatMessage(translations.totals.ofWhichVat)
                    : intl.formatMessage(translations.totals.vat)
                } ${
                  projection.is_vat_shifted
                    ? intl.formatMessage(translations.totals.shiftedVat)
                    : ''
                } ${vat.rate}%`
              : `${intl.formatMessage(translations.totals.vat)} ${
                  projection.is_vat_shifted
                    ? intl.formatMessage(translations.totals.shiftedVat)
                    : ''
                } ${vat.rate}%`}
          </TotalsRowDescription>
          <TotalsRowValue variant="subtitle2">
            {formatCurrency(
              (shouldShowVatAsNegative ? vat.amount * -1 : vat.amount) / 100,
              projection?.currency ?? 'EUR',
            )}
          </TotalsRowValue>
        </TotalsRow>
      ))}

      {!shouldShowVatBelowTotal && (
        <TotalsRow>
          <TotalsRowDescription variant="body1">
            {intl.formatMessage(translations.totals.total)}
          </TotalsRowDescription>
          <TotalsRowValue variant="subtitle2">
            {formatCurrency((projection?.total ?? 0) / 100, projection?.currency ?? 'EUR')}
          </TotalsRowValue>
        </TotalsRow>
      )}
    </>
  );
};

ProjectionList.propTypes = {
  projection: PropTypes.object.isRequired,
};

export default ProjectionList;
